import { Avatar, Button, Flex, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { type CustomerMatchCandidate, displayNameOf } from "./customer-match.ts"
import { REASON_LABEL } from "./lead-match-vocabulary.ts"

interface LeadWizardCustomerRowProps {
  candidate: CustomerMatchCandidate
  actionLabel: string
  onUse: () => void
  busy?: boolean
  withReason?: boolean
}

export function LeadWizardCustomerRow({
  candidate,
  actionLabel,
  onUse,
  busy,
  withReason
}: LeadWizardCustomerRowProps) {
  const { t } = useTranslation()
  const name = displayNameOf(candidate)
  const handles = [candidate.email, candidate.phone].filter(Boolean).join(" · ")
  const initials = [candidate.first_name, candidate.last_name]
    .filter(Boolean)
    .map((part) => part?.trim().charAt(0).toUpperCase())
    .join("")
    .slice(0, 2)

  return (
    <Flex
      gap={"small"}
      align={"center"}
      justify={"space-between"}
      wrap
      className={"rounded-lg border border-black/10 px-3 py-2"}
    >
      <Avatar>{initials || "?"}</Avatar>
      <Flex vertical gap={2} className={"min-w-0 flex-1"}>
        <Flex gap={"small"} align={"center"} wrap>
          <Typography.Text strong>
            {name || t("leadWizard.duplicate.unnamed")}
          </Typography.Text>
          {withReason && (
            <Tag color={"gold"}>{t(REASON_LABEL[candidate.reason])}</Tag>
          )}
          {candidate.is_suspended && (
            <Tag color={"red"}>{t("leadWizard.duplicate.suspended")}</Tag>
          )}
          {candidate.is_incomplete && (
            <Tag>{t("leadWizard.duplicate.incomplete")}</Tag>
          )}
          {typeof candidate.project_count === "number" && (
            <Tag>
              {t("leadWizard.duplicate.projects", {
                count: candidate.project_count
              })}
            </Tag>
          )}
        </Flex>
        {handles && (
          <Typography.Text type={"secondary"} className={"text-xs"}>
            {handles}
          </Typography.Text>
        )}
        {candidate.is_suspended && (
          <Typography.Text type={"danger"} className={"text-xs"}>
            {t("leadWizard.duplicate.suspendedHint")}
          </Typography.Text>
        )}
      </Flex>

      {/* The create call refuses a suspended customer outright, so offering the
          link would only spend the rest of the wizard on a guaranteed rejection. */}
      <Button
        size={"small"}
        loading={busy}
        disabled={!!candidate.is_suspended}
        onClick={onUse}
        className={"min-h-9"}
      >
        {actionLabel}
      </Button>
    </Flex>
  )
}
